export default function (element, data) {
    if (window.innerWidth <= 960) {
        return;
    }

    const backgroundColor = data.backgroundColor;
    const foregroundColor = data.foregroundColor;
    const circleRadius = 60;
    const gaussianBlurRadius = 90;
    const gradientRadius = getVisibleRadius(circleRadius, gaussianBlurRadius);
    const gradientColors = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
        .map((step, index) => {
            const radius = step === 1 ? gradientRadius : gradientRadius * step;
            const blur = step === 1 ? 75 : gaussianBlurRadius;
            const color = calculateBlurredCircleColor(
                radius,
                circleRadius,
                blur,
                backgroundColor,
                foregroundColor
            );
            return `${color} ${index * 10}%`;
        })
        .join(',\n');

    let lastMouseClientX = null;
    let lastMouseClientY = null;

    function isElementVisible() {
        const rect = element.getBoundingClientRect();
        return (
            rect.top < window.innerHeight &&
            rect.bottom > 0 &&
            rect.left < window.innerWidth &&
            rect.right > 0
        );
    }

    function updateGradient() {
        if (lastMouseClientX === null || lastMouseClientY === null) {
            return;
        }

        const rect = element.getBoundingClientRect();
        const mouseX = lastMouseClientX - rect.left;
        const mouseY = lastMouseClientY - rect.top;

        const radialGradient = `radial-gradient(circle ${gradientRadius}px at ${mouseX}px ${mouseY}px, ${gradientColors})`;
        element.style.backgroundImage = radialGradient;
    }

    window.addEventListener("mousemove", (event) => {
        lastMouseClientX = event.clientX;
        lastMouseClientY = event.clientY;

        if (isElementVisible()) {
            updateGradient();
        }
    });

    window.addEventListener("scroll", () => {
        if (isElementVisible()) {
            updateGradient();
        }
    }, { passive: true });
}

// Радиус, на котором влияние рамытого круга можно различить
function getVisibleRadius(r, b, threshold = 0.004) {
    // threshold - порог видимости (по умолчанию 1/255)
    const sigma = b;

    // Решаем уравнение: 0.5 * (1 + erf(x)) = threshold
    // erf(x) = 2 * threshold - 1
    const erfTarget = 2 * threshold - 1;

    // Для малых threshold: erfTarget ≈ -1
    // Обратная функция erf (приближение для больших отрицательных значений)
    // erf(x) ≈ -1 означает x << -3

    // Более точное приближение через inverf
    const x = inverf(erfTarget);

    // (r - d) / (sqrt(2) * sigma) = x
    // d = r - x * sqrt(2) * sigma
    const d = r - x * Math.sqrt(2) * sigma;

    return d;
}

function calculateBlurredCircleColor(d, r, b, c1Hex, c2Hex) {
    // x, y - координаты точки
    // r - радиус круга
    // b - радиус размытия
    // c1 - цвет фона {r, g, b} (0-255)
    // c2 - цвет круга {r, g, b} (0-255)

    // Расстояние от центра (предполагаем центр в (0, 0))
    //const d = Math.sqrt(x * x + y * y);

    const c1 = parseHexColor(c1Hex);
    const c2 = parseHexColor(c2Hex);

    // Стандартное отклонение гауссиана
    const sigma = b;

    // Вычисляем интенсность через функцию ошибок
    // erf((r - d) / (sqrt(2) * sigma))
    const arg = (r - d) / (Math.sqrt(2) * sigma);
    const erfValue = erf(arg);

    // Интенсивность от 0 (фон) до 1 (круг)
    const intensity = 0.5 * (1 + erfValue);

    // Интерполируем цвет
    const r_val = c1.r + (c2.r - c1.r) * intensity;
    const g_val = c1.g + (c2.g - c1.g) * intensity;
    const b_val = c1.b + (c2.b - c1.b) * intensity;

    // Возвращаем в формате CSS rgba
    return `rgba(${Math.round(r_val)}, ${Math.round(g_val)}, ${Math.round(b_val)}, 1)`;
}

// Функция ошибок
// Приближение через ряд Тейлора
function erf(x) {
    // Для больших значений возвращаем предел
    if (x > 5) return 1;
    if (x < -5) return -1;

    // Приближение Abramowitz and Stegun (точность ~1.5e-7)
    const sign = x >= 0 ? 1 : -1;
    x = Math.abs(x);

    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
}

// Обратная функция ошибок (приближение)
function inverf(y) {
    // Для y близких к -1 (малая интенсивность)
    if (y < -0.99) {
        // Используем асимптотическое приближение
        const ln = Math.log(1 - y * y);
        return -Math.sqrt(-ln - Math.log(Math.PI) / 2);
    }

    // Для общего случая - итерационный метод Ньютона
    let x = 0;
    if (y > 0) x = Math.sqrt(-Math.log(1 - y));
    else x = -Math.sqrt(-Math.log(1 + y));

    // Несколько итераций Ньютона для уточнения
    for (let i = 0; i < 3; i++) {
        const fx = erf(x) - y;
        const fpx = (2 / Math.sqrt(Math.PI)) * Math.exp(-x * x);
        x = x - fx / fpx;
    }

    return x;
}

function parseHexColor(hex) {
    // Убираем # если есть
    hex = hex.replace(/^#/, '');

    // Поддержка короткой формы (#RGB -> #RRGGBB)
    if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return { r, g, b };
}